import React, { useState } from 'react';
import RadioButton, { RadioButtonLarge, RadioButtonMedium } from '@ajat/design-system/RadioButton';
import { ComponentPage, DemoCard, Demo } from '../shared';

export const meta = {
  component: RadioButton,
  componentName: 'RadioButton',
  title: 'RadioButton',
  group: 'Forms',
  importPath: '@ajat/design-system/RadioButton',
  description:
    'Single-select control in two sizes. Large uses a 32px selector with a success-green fill and carries an optional description — it is the delivery / payment choice in checkout. Medium uses a 24px selector with a black fill for denser lists.',
  props: [
    ['size', "'large' | 'medium'", "'medium'", 'Selector size and fill colour'],
    ['title', 'string', '—', 'Primary label'],
    ['description', 'string', 'null', 'Secondary line under the title'],
    ['checked', 'bool', 'false', 'Selected state'],
    ['disabled', 'bool', 'false', 'Disables the control'],
    ['name', 'string', '—', 'Radio group name — shared by every option in a group'],
    ['value', 'string', '—', 'Value submitted for this option'],
    ['onChange', 'func', '—', 'Fires when the option is picked'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'RadioButtonLarge and RadioButtonMedium are pass-through wrappers that pin size — they take exactly these props minus size.',
  usage: [
    {
      title: 'A group',
      code: `import { RadioButtonLarge } from '@ajat/design-system/RadioButton';

const [delivery, setDelivery] = useState('school');

{DELIVERY_OPTIONS.map((option) => (
  <RadioButtonLarge
    key={option.id}
    name="delivery"          // same name across the group
    value={option.id}
    title={option.title}
    description={option.description}
    checked={delivery === option.id}
    onChange={() => setDelivery(option.id)}
  />
))}`,
    },
  ],
};

const DELIVERY = [
  { id: 'option-1', title: 'Deliver to school', description: 'Free of charge.' },
  { id: 'option-2', title: 'Deliver to home', description: 'Standard shipping rates apply.' },
];

export default function RadioButtonPage() {
  const [large, setLarge] = useState('option-1');
  const [medium, setMedium] = useState('option-1');

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Large" hint="32px selector · success-green fill">
        <div className="flex flex-col gap-[var(--space-ui-medium-l)]">
          <Demo caption="DEFAULT"><RadioButtonLarge title="Standard delivery" /></Demo>
          <Demo caption="HOVER"><RadioButtonLarge className="ds-radio--preview-hover" title="Standard delivery" /></Demo>
          <Demo caption="SELECTED"><RadioButtonLarge title="Standard delivery" checked /></Demo>
          <Demo caption="DISABLED"><RadioButtonLarge title="Standard delivery" disabled /></Demo>
          <Demo caption="WITH DESCRIPTION">
            <RadioButtonLarge
              title="Express delivery"
              description="Arrives in 1–2 business days. Additional fees may apply."
              checked
            />
          </Demo>
        </div>
      </DemoCard>

      <DemoCard title="Medium" hint="24px selector · gray-10 fill">
        <div className="flex flex-col gap-[var(--space-ui-medium-l)]">
          <Demo caption="DEFAULT"><RadioButtonMedium title="Refund" /></Demo>
          <Demo caption="HOVER"><RadioButtonMedium className="ds-radio--preview-hover" title="Refund" /></Demo>
          <Demo caption="SELECTED"><RadioButtonMedium title="Refund" checked /></Demo>
          <Demo caption="DISABLED"><RadioButtonMedium title="Refund" disabled /></Demo>
          <Demo caption="WITH DESCRIPTION">
            <RadioButtonMedium title="New item" description="We'll send a replacement at no extra cost." checked />
          </Demo>
        </div>
      </DemoCard>

      <DemoCard title="Interactive group — large">
        <div className="flex flex-col gap-[var(--space-ui-small-l)]">
          {DELIVERY.map((o) => (
            <RadioButtonLarge
              key={o.id}
              name="rb-large-demo"
              value={o.id}
              title={o.title}
              description={o.description}
              checked={large === o.id}
              onChange={() => setLarge(o.id)}
            />
          ))}
        </div>
      </DemoCard>

      <DemoCard title="Interactive group — medium">
        <div className="flex flex-col gap-[var(--space-ui-small-l)]">
          {[{ id: 'option-1', title: 'Refund' }, { id: 'option-2', title: 'New item' }].map((o) => (
            <RadioButtonMedium
              key={o.id}
              name="rb-medium-demo"
              value={o.id}
              title={o.title}
              checked={medium === o.id}
              onChange={() => setMedium(o.id)}
            />
          ))}
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
